import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Loader2, Zap, Clock, RefreshCw, Shield, History } from 'lucide-react';
import type { Controller } from '@/types';
import { swatidService } from '@/services';
import { toSwatidMode, canControlSwatidRelays } from '@/utils/controllerType';
import type { SwatidOperationMode } from '@/constants/swatid';
import {
  useSwatidStatus,
  useSwatidInfo,
  useSwatidRelays,
  useSwatidRefresh,
} from '@/hooks/useSwatidController';
import { SwatidStatusBar } from './SwatidStatusBar';
import { SwatidModeSelector } from './SwatidModeSelector';
import { SwatidRelayGrid } from './SwatidRelayGrid';
import { SwatidSchedulesSection } from './SwatidSchedulesSection';
import { SwatidSupervisionSection } from './SwatidSupervisionSection';
import { SwatidEventsSection } from './SwatidEventsSection';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

interface SwatidControllerDetailProps {
  controller: Controller;
  companyName?: string;
}

export function SwatidControllerDetail({ controller, companyName }: SwatidControllerDetailProps) {
  const { toast } = useToast();
  const { refreshAll, refreshStatus } = useSwatidRefresh(controller.id);
  const [activeTab, setActiveTab] = useState('relays');

  const enabled = true;
  const { data: statusData, isLoading: statusLoading, isFetching: statusFetching } =
    useSwatidStatus(controller.id, enabled);
  const { data: infoData } = useSwatidInfo(controller.id, enabled);
  const { data: relays, isLoading: relaysLoading } = useSwatidRelays(controller.id, enabled);

  const currentMode: SwatidOperationMode = statusData?.mode
    ? toSwatidMode(statusData.mode)
    : infoData?.mode
      ? toSwatidMode(infoData.mode)
      : toSwatidMode(controller.operationMode);

  const isOnline = controller.status === 'online';
  const canControlRelays = canControlSwatidRelays(currentMode) && isOnline;
  const canEditSchedules = currentMode === 'local' || currentMode === 'remota';

  const modeMutation = useMutation({
    mutationFn: async (mode: SwatidOperationMode) => {
      const res = await swatidService.setMode(controller.id, { mode });
      if (!res.success) throw new Error(res.error?.message || 'Error al cambiar modo');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      toast({ title: 'Modo actualizado' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const refreshStatusMutation = useMutation({
    mutationFn: async () => {
      const res = await swatidService.getStatus(controller.id);
      if (!res.success) throw new Error(res.error?.message || 'Error al actualizar estado');
      return res.data;
    },
    onSuccess: async () => {
      await refreshStatus();
      toast({ title: 'Estado actualizado' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const refreshRelaysMutation = useMutation({
    mutationFn: async () => {
      const res = await swatidService.refreshRelays(controller.id);
      if (!res.success) throw new Error(res.error?.message || 'Error al sincronizar relés');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      toast({
        title: 'Sincronización iniciada',
        description: 'La configuración de relés se actualizará al responder el dispositivo.',
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const loading = statusLoading || relaysLoading;

  return (
    <div className="space-y-4">
      <SwatidStatusBar
        controllerStatus={controller.status}
        serialNumber={controller.serialNumber || statusData?.serialNumber}
        statusData={statusData}
        lastSeen={controller.lastSeen?.toISOString()}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => refreshStatusMutation.mutate()}
          disabled={refreshStatusMutation.isPending || statusFetching}
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${refreshStatusMutation.isPending || statusFetching ? 'animate-spin' : ''}`}
          />
          Actualizar estado
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refreshRelaysMutation.mutate()}
          disabled={refreshRelaysMutation.isPending}
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${refreshRelaysMutation.isPending ? 'animate-spin' : ''}`}
          />
          Sincronizar relés
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-muted-foreground font-medium">Modo de operación</p>
        <SwatidModeSelector
          currentMode={currentMode}
          onModeChange={(m) => modeMutation.mutate(m)}
          isLoading={modeMutation.isPending}
          disabled={!isOnline}
        />
        {!canControlRelays && isOnline && currentMode !== 'remota' && (
          <p className="text-xs text-muted-foreground">
            Cambie a modo <strong>Remota</strong> para controlar relés desde la plataforma.
          </p>
        )}
      </div>

      {companyName && (
        <p className="text-sm text-muted-foreground">
          Empresa: <span className="text-foreground">{companyName}</span>
        </p>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
          <TabsTrigger value="relays" className="gap-2 text-xs sm:text-sm">
            <Zap className="w-4 h-4 shrink-0" />
            Relés
          </TabsTrigger>
          <TabsTrigger value="schedules" className="gap-2 text-xs sm:text-sm">
            <Clock className="w-4 h-4 shrink-0" />
            Programaciones
          </TabsTrigger>
          <TabsTrigger value="supervision" className="gap-2 text-xs sm:text-sm">
            <Shield className="w-4 h-4 shrink-0" />
            Supervisión
          </TabsTrigger>
          <TabsTrigger value="events" className="gap-2 text-xs sm:text-sm">
            <History className="w-4 h-4 shrink-0" />
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="relays" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : relays && relays.length > 0 ? (
            <SwatidRelayGrid
              controllerId={controller.id}
              relays={relays}
              canControl={canControlRelays}
              isOnline={isOnline}
              currentMode={currentMode}
            />
          ) : (
            <Skeleton className="h-32 w-full" />
          )}
        </TabsContent>

        <TabsContent value="schedules" className="mt-4">
          <SwatidSchedulesSection
            controllerId={controller.id}
            editable={canEditSchedules}
            currentMode={currentMode}
          />
        </TabsContent>

        <TabsContent value="supervision" className="mt-4">
          <SwatidSupervisionSection
            controllerId={controller.id}
            currentMode={currentMode}
            isOnline={isOnline}
            onRequestLocalMode={() => modeMutation.mutate('local')}
          />
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <SwatidEventsSection controllerId={controller.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
